import React, { useEffect } from "react";
import { Box, Button, CircularProgress, Dialog, Stack, TextField, Typography, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSnackbar } from "src/utils/useSnackbar";
import { useCreateIcv } from "src/api/client-api/icv/useCreateIcv";
import { Icv } from "src/api/client-api/icv/useAllIcvs";
import { UnitAutocomplete } from "src/components/Selector/UnitSelector/UnitAutocomplete";
import { useGetOneUnit } from "src/api/sure-api/units/useGetOneUnit";
import { getApiUrl } from "src/api/axiosClient/utils";

// Zod schema for Icv form validation
const IcvFormSchema = z.object({
    id: z.number().optional(),
    name: z.string().min(1, t("icvs.validation.nameRequired", "Name is required")),
    code: z.string().min(1, t("icvs.validation.codeRequired", "Code is required")),
    description: z.string().optional(),
    groupName: z.string().optional(),
    unit: z
        .object({
            id: z.number(),
            title: z.string(),
            code: z.string(),
        })
        .nullable()
        .refine((val) => val !== null, { message: t("icvs.validation.unitRequired", "Unit is required") }),
    climate_change: z.number(),
    ozone_depletion: z.number(),
    ionising_radiation: z.number(),
    photochemical_ozone_formation: z.number(),
    particulate_matter: z.number(),
    human_toxicity_non_cancer: z.number(),
    human_toxicity_cancer: z.number(),
    acidification: z.number(),
    eutrophication_fresh_water: z.number(),
    eutrophication_marine: z.number(),
    eutrophication_terrestrial: z.number(),
    ecotoxicity_freshwater: z.number(),
    land_use: z.number(),
    water_use: z.number(),
    resource_use_fossils: z.number(),
    resource_use_minerals_metals: z.number(),
    climate_change_fossil: z.number(),
    climate_change_biogenic: z.number(),
    climate_change_land_use_lu_change: z.number(),
    human_toxicity_non_cancer_organics: z.number(),
    human_toxicity_non_cancer_inorganics: z.number(),
    human_toxicity_non_cancer_metals: z.number(),
    human_toxicity_cancer_organics: z.number(),
    human_toxicity_cancer_inorganics: z.number(),
    human_toxicity_cancer_metals: z.number(),
    ecotoxicity_freshwater_organics: z.number(),
    ecotoxicity_freshwater_inorganics: z.number(),
    ecotoxicity_freshwater_metals: z.number(),
});

type IcvFormInput = z.input<typeof IcvFormSchema>;
type IcvFormData = z.output<typeof IcvFormSchema>;

interface IcvModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editingIcv?: Icv | null;
}

const FloatField = ({
    name,
    label,
    control,
    errors,
    t,
}: {
    name: keyof IcvFormInput;
    label: string;
    control: any;
    errors: any;
    t: any;
}) => {
    return (
        <Box>
            <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                {label}:
            </Typography>
            <Controller
                name={name}
                control={control}
                render={({ field: { value, onChange, onBlur, ref, name: fieldName } }) => (
                    <TextField
                        name={fieldName}
                        inputRef={ref}
                        fullWidth
                        type="number"
                        inputProps={{ step: "any" }}
                        error={!!errors[name]}
                        helperText={errors[name]?.message}
                        value={value != null ? value : ""}
                        onChange={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue === "" || inputValue === "-" || inputValue === ".") {
                                onChange(0);
                            } else {
                                const numValue = parseFloat(inputValue);
                                if (!isNaN(numValue)) {
                                    onChange(numValue);
                                }
                            }
                        }}
                        onBlur={onBlur}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "24px",
                                minHeight: "40px",
                                backgroundColor: "white",
                                "& fieldset": {
                                    borderColor: "#E7E6E4",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#D0D0D0",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "primary.main",
                                },
                            },
                            "& .MuiInputBase-input": {
                                fontSize: "0.875rem",
                                padding: "0px 16px !important",
                                color: "#393A36",
                            },
                        }}
                    />
                )}
            />
        </Box>
    );
};

export const IcvModal: React.FC<IcvModalProps> = ({ open, onClose, onSuccess, editingIcv }) => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();

    const createIcvMutation = useCreateIcv();
    const isProcessing = createIcvMutation.isLoading;

    const isEditing = !!editingIcv;

    // Load unit when editing
    const { unit: loadedUnit } = useGetOneUnit(editingIcv?.unitCode, {
        enabled: !!editingIcv?.unitCode && open,
    });

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
    } = useForm<IcvFormInput, unknown, IcvFormData>({
        mode: "onChange",
        resolver: zodResolver(IcvFormSchema),
        defaultValues: {
            id: undefined,
            name: "",
            code: "",
            description: "",
            groupName: "",
            unit: null,
            climate_change: 0,
            ozone_depletion: 0,
            ionising_radiation: 0,
            photochemical_ozone_formation: 0,
            particulate_matter: 0,
            human_toxicity_non_cancer: 0,
            human_toxicity_cancer: 0,
            acidification: 0,
            eutrophication_fresh_water: 0,
            eutrophication_marine: 0,
            eutrophication_terrestrial: 0,
            ecotoxicity_freshwater: 0,
            land_use: 0,
            water_use: 0,
            resource_use_fossils: 0,
            resource_use_minerals_metals: 0,
            climate_change_fossil: 0,
            climate_change_biogenic: 0,
            climate_change_land_use_lu_change: 0,
            human_toxicity_non_cancer_organics: 0,
            human_toxicity_non_cancer_inorganics: 0,
            human_toxicity_non_cancer_metals: 0,
            human_toxicity_cancer_organics: 0,
            human_toxicity_cancer_inorganics: 0,
            human_toxicity_cancer_metals: 0,
            ecotoxicity_freshwater_organics: 0,
            ecotoxicity_freshwater_inorganics: 0,
            ecotoxicity_freshwater_metals: 0,
        },
    });

    // Reset form when modal opens or editingIcv changes
    useEffect(() => {
        if (open) {
            if (editingIcv) {
                // Pre-fill form for editing
                reset({
                    id: editingIcv.id,
                    name: editingIcv.name || "",
                    code: editingIcv.code || "",
                    description: editingIcv.description || "",
                    groupName: editingIcv.groupName || "",
                    unit: loadedUnit || null,
                    climate_change: editingIcv.climate_change || 0,
                    ozone_depletion: editingIcv.ozone_depletion || 0,
                    ionising_radiation: editingIcv.ionising_radiation || 0,
                    photochemical_ozone_formation: editingIcv.photochemical_ozone_formation || 0,
                    particulate_matter: editingIcv.particulate_matter || 0,
                    human_toxicity_non_cancer: editingIcv.human_toxicity_non_cancer || 0,
                    human_toxicity_cancer: editingIcv.human_toxicity_cancer || 0,
                    acidification: editingIcv.acidification || 0,
                    eutrophication_fresh_water: editingIcv.eutrophication_fresh_water || 0,
                    eutrophication_marine: editingIcv.eutrophication_marine || 0,
                    eutrophication_terrestrial: editingIcv.eutrophication_terrestrial || 0,
                    ecotoxicity_freshwater: editingIcv.ecotoxicity_freshwater || 0,
                    land_use: editingIcv.land_use || 0,
                    water_use: editingIcv.water_use || 0,
                    resource_use_fossils: editingIcv.resource_use_fossils || 0,
                    resource_use_minerals_metals: editingIcv.resource_use_minerals_metals || 0,
                    climate_change_fossil: editingIcv.climate_change_fossil || 0,
                    climate_change_biogenic: editingIcv.climate_change_biogenic || 0,
                    climate_change_land_use_lu_change: editingIcv.climate_change_land_use_lu_change || 0,
                    human_toxicity_non_cancer_organics: editingIcv.human_toxicity_non_cancer_organics || 0,
                    human_toxicity_non_cancer_inorganics: editingIcv.human_toxicity_non_cancer_inorganics || 0,
                    human_toxicity_non_cancer_metals: editingIcv.human_toxicity_non_cancer_metals || 0,
                    human_toxicity_cancer_organics: editingIcv.human_toxicity_cancer_organics || 0,
                    human_toxicity_cancer_inorganics: editingIcv.human_toxicity_cancer_inorganics || 0,
                    human_toxicity_cancer_metals: editingIcv.human_toxicity_cancer_metals || 0,
                    ecotoxicity_freshwater_organics: editingIcv.ecotoxicity_freshwater_organics || 0,
                    ecotoxicity_freshwater_inorganics: editingIcv.ecotoxicity_freshwater_inorganics || 0,
                    ecotoxicity_freshwater_metals: editingIcv.ecotoxicity_freshwater_metals || 0,
                });
            } else {
                // Reset form for creating
                reset({
                    id: undefined,
                    name: "",
                    code: "",
                    description: "",
                    groupName: "",
                    unit: null,
                    climate_change: 0,
                    ozone_depletion: 0,
                    ionising_radiation: 0,
                    photochemical_ozone_formation: 0,
                    particulate_matter: 0,
                    human_toxicity_non_cancer: 0,
                    human_toxicity_cancer: 0,
                    acidification: 0,
                    eutrophication_fresh_water: 0,
                    eutrophication_marine: 0,
                    eutrophication_terrestrial: 0,
                    ecotoxicity_freshwater: 0,
                    land_use: 0,
                    water_use: 0,
                    resource_use_fossils: 0,
                    resource_use_minerals_metals: 0,
                    climate_change_fossil: 0,
                    climate_change_biogenic: 0,
                    climate_change_land_use_lu_change: 0,
                    human_toxicity_non_cancer_organics: 0,
                    human_toxicity_non_cancer_inorganics: 0,
                    human_toxicity_non_cancer_metals: 0,
                    human_toxicity_cancer_organics: 0,
                    human_toxicity_cancer_inorganics: 0,
                    human_toxicity_cancer_metals: 0,
                    ecotoxicity_freshwater_organics: 0,
                    ecotoxicity_freshwater_inorganics: 0,
                    ecotoxicity_freshwater_metals: 0,
                });
            }
        }
    }, [open, editingIcv, reset, loadedUnit]);

    const onSubmitHandler: SubmitHandler<IcvFormData> = async (formData) => {
        try {
            if (!formData.unit) {
                setSnackbarProps({
                    message: t("icvs.unitRequired", "Unit is required"),
                    type: "error",
                });
                return;
            }

            const icvData: any = {
                name: formData.name.trim(),
                code: formData.code.trim(),
                description: formData.description?.trim() || undefined,
                groupName: formData.groupName?.trim() || undefined,
                unitCode: formData.unit.code,
                climate_change: formData.climate_change ?? 0,
                ozone_depletion: formData.ozone_depletion ?? 0,
                ionising_radiation: formData.ionising_radiation ?? 0,
                photochemical_ozone_formation: formData.photochemical_ozone_formation ?? 0,
                particulate_matter: formData.particulate_matter ?? 0,
                human_toxicity_non_cancer: formData.human_toxicity_non_cancer ?? 0,
                human_toxicity_cancer: formData.human_toxicity_cancer ?? 0,
                acidification: formData.acidification ?? 0,
                eutrophication_fresh_water: formData.eutrophication_fresh_water ?? 0,
                eutrophication_marine: formData.eutrophication_marine ?? 0,
                eutrophication_terrestrial: formData.eutrophication_terrestrial ?? 0,
                ecotoxicity_freshwater: formData.ecotoxicity_freshwater ?? 0,
                land_use: formData.land_use ?? 0,
                water_use: formData.water_use ?? 0,
                resource_use_fossils: formData.resource_use_fossils ?? 0,
                resource_use_minerals_metals: formData.resource_use_minerals_metals ?? 0,
                climate_change_fossil: formData.climate_change_fossil ?? 0,
                climate_change_biogenic: formData.climate_change_biogenic ?? 0,
                climate_change_land_use_lu_change: formData.climate_change_land_use_lu_change ?? 0,
                human_toxicity_non_cancer_organics: formData.human_toxicity_non_cancer_organics ?? 0,
                human_toxicity_non_cancer_inorganics: formData.human_toxicity_non_cancer_inorganics ?? 0,
                human_toxicity_non_cancer_metals: formData.human_toxicity_non_cancer_metals ?? 0,
                human_toxicity_cancer_organics: formData.human_toxicity_cancer_organics ?? 0,
                human_toxicity_cancer_inorganics: formData.human_toxicity_cancer_inorganics ?? 0,
                human_toxicity_cancer_metals: formData.human_toxicity_cancer_metals ?? 0,
                ecotoxicity_freshwater_organics: formData.ecotoxicity_freshwater_organics ?? 0,
                ecotoxicity_freshwater_inorganics: formData.ecotoxicity_freshwater_inorganics ?? 0,
                ecotoxicity_freshwater_metals: formData.ecotoxicity_freshwater_metals ?? 0,
                ...(isEditing ? { id: formData.id } : {}),
            };

            console.info("Sending ICV data:", icvData);
            console.info("Data size:", JSON.stringify(icvData).length, "bytes");
            console.info("API URL:", getApiUrl());
            console.info("Full endpoint:", `${getApiUrl()}/api/icvs`);
            console.info("Current hostname:", window.location.hostname);

            await createIcvMutation.mutateAsync({
                icv: icvData,
            });

            setSnackbarProps({
                message:
                    isEditing ?
                        t("icvs.updateSuccess", "ICV updated successfully")
                    :   t("icvs.createSuccess", "ICV created successfully"),
                type: "success",
            });

            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("ICV creation/update error:", error);
            console.error("Error details:", {
                message: error?.message,
                response: error?.response,
                request: error?.request,
                config: error?.config,
            });

            // Extract error message from API response
            let errorMessage: string =
                isEditing ?
                    t("icvs.updateError", "Failed to update ICV")
                :   t("icvs.createError", "Failed to create ICV");

            // Handle network errors
            if (error?.code === "ERR_NETWORK" || error?.message?.includes("Network Error")) {
                errorMessage = t("icvs.networkError", "Network error. Please check your connection and try again.");
            } else if (error?.response?.data) {
                // Try to extract a meaningful error message
                const errorData = error.response.data;
                if (errorData["hydra:description"]) {
                    errorMessage = errorData["hydra:description"];
                } else if (errorData["hydra:title"]) {
                    errorMessage = errorData["hydra:title"];
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.violations && Array.isArray(errorData.violations)) {
                    // Format validation errors
                    const violations = errorData.violations
                        .map((v: any) => `${v.propertyPath}: ${v.message}`)
                        .join(", ");
                    errorMessage = `${errorMessage}: ${violations}`;
                }
            } else if (error?.message) {
                errorMessage = `${errorMessage}: ${error.message}`;
            }

            setSnackbarProps({
                message: errorMessage,
                type: "error",
            });
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                    maxHeight: "90vh",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                        {isEditing ? t("icvs.editTitle", "Edit ICV") : t("icvs.createTitle", "Create ICV")}
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                        {isEditing ?
                            t("icvs.editDescription", "Update the ICV information.")
                        :   t("icvs.createDescription", "Set up a new ICV.")}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        cursor: "pointer",
                        padding: "8px",
                        borderRadius: "50%",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                    onClick={handleClose}
                >
                    <CloseIcon />
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ padding: "32px", maxHeight: "calc(90vh - 200px)", overflowY: "auto" }}>
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                    <Stack spacing={3}>
                        {/* Basic Information */}
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "16px" }}
                            >
                                {t("icvs.basicInformation", "Basic Information")}
                            </Typography>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("icvs.nameLabel", "Name")}*:
                                    </Typography>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                error={!!errors.name}
                                                helperText={errors.name?.message}
                                                placeholder={t("icvs.namePlaceholder", "Enter name")}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "24px",
                                                        minHeight: "40px",
                                                        backgroundColor: "white",
                                                        "& fieldset": {
                                                            borderColor: "#E7E6E4",
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: "#D0D0D0",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: "primary.main",
                                                        },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.875rem",
                                                        padding: "0px 16px !important",
                                                        color: "#393A36",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>

                                <Stack direction="row" spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                        >
                                            {t("icvs.codeLabel", "Code")}*:
                                        </Typography>
                                        <Controller
                                            name="code"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    error={!!errors.code}
                                                    helperText={errors.code?.message}
                                                    placeholder={t("icvs.codePlaceholder", "Enter code")}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "24px",
                                                            minHeight: "40px",
                                                            backgroundColor: "white",
                                                            "& fieldset": {
                                                                borderColor: "#E7E6E4",
                                                            },
                                                            "&:hover fieldset": {
                                                                borderColor: "#D0D0D0",
                                                            },
                                                            "&.Mui-focused fieldset": {
                                                                borderColor: "primary.main",
                                                            },
                                                        },
                                                        "& .MuiInputBase-input": {
                                                            fontSize: "0.875rem",
                                                            padding: "0px 16px !important",
                                                            color: "#393A36",
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>

                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                        >
                                            {t("icvs.unitIdLabel", "Unit")}*:
                                        </Typography>
                                        <Controller
                                            name="unit"
                                            control={control}
                                            render={({ field }) => (
                                                <UnitAutocomplete
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    placeholder={t("icvs.unitIdPlaceholder", "Search for a unit")}
                                                    error={!!errors.unit}
                                                    helperText={errors.unit?.message}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Stack>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("icvs.descriptionLabel", "Description")}:
                                    </Typography>
                                    <Controller
                                        name="description"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                multiline
                                                rows={3}
                                                placeholder={t("icvs.descriptionPlaceholder", "Enter description")}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "24px",
                                                        backgroundColor: "white",
                                                        "& fieldset": {
                                                            borderColor: "#E7E6E4",
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: "#D0D0D0",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: "primary.main",
                                                        },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.875rem",
                                                        padding: "16px !important",
                                                        color: "#393A36",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("icvs.groupNameLabel", "Group Name")}:
                                    </Typography>
                                    <Controller
                                        name="groupName"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                placeholder={t("icvs.groupNamePlaceholder", "Enter group name")}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "24px",
                                                        minHeight: "40px",
                                                        backgroundColor: "white",
                                                        "& fieldset": {
                                                            borderColor: "#E7E6E4",
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: "#D0D0D0",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: "primary.main",
                                                        },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.875rem",
                                                        padding: "0px 16px !important",
                                                        color: "#393A36",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        {/* Climate Change */}
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "16px" }}
                            >
                                {t("icvs.climateChange", "Climate Change")}
                            </Typography>
                            <Stack spacing={2} direction="row" flexWrap="wrap" useFlexGap>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="climate_change"
                                        label={t("icvs.climateChange", "Climate Change")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="climate_change_fossil"
                                        label={t("icvs.climateChangeFossil", "Climate Change Fossil")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="climate_change_biogenic"
                                        label={t("icvs.climateChangeBiogenic", "Climate Change Biogenic")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="climate_change_land_use_lu_change"
                                        label={t("icvs.climateChangeLandUse", "Climate Change Land Use")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        {/* Ozone & Radiation */}
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "16px" }}
                            >
                                {t("icvs.ozoneRadiation", "Ozone & Radiation")}
                            </Typography>
                            <Stack spacing={2} direction="row" flexWrap="wrap" useFlexGap>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="ozone_depletion"
                                        label={t("icvs.ozoneDepletion", "Ozone Depletion")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="ionising_radiation"
                                        label={t("icvs.ionisingRadiation", "Ionising Radiation")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="photochemical_ozone_formation"
                                        label={t("icvs.photochemicalOzoneFormation", "Photochemical Ozone Formation")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="particulate_matter"
                                        label={t("icvs.particulateMatter", "Particulate Matter")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        {/* Human Toxicity */}
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "16px" }}
                            >
                                {t("icvs.humanToxicity", "Human Toxicity")}
                            </Typography>
                            <Stack spacing={2} direction="row" flexWrap="wrap" useFlexGap>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="human_toxicity_non_cancer"
                                        label={t("icvs.humanToxicityNonCancer", "Human Toxicity Non Cancer")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="human_toxicity_non_cancer_organics"
                                        label={t(
                                            "icvs.humanToxicityNonCancerOrganics",
                                            "Human Toxicity Non Cancer Organics",
                                        )}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="human_toxicity_non_cancer_inorganics"
                                        label={t(
                                            "icvs.humanToxicityNonCancerInorganics",
                                            "Human Toxicity Non Cancer Inorganics",
                                        )}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="human_toxicity_non_cancer_metals"
                                        label={t(
                                            "icvs.humanToxicityNonCancerMetals",
                                            "Human Toxicity Non Cancer Metals",
                                        )}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="human_toxicity_cancer"
                                        label={t("icvs.humanToxicityCancer", "Human Toxicity Cancer")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="human_toxicity_cancer_organics"
                                        label={t("icvs.humanToxicityCancerOrganics", "Human Toxicity Cancer Organics")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="human_toxicity_cancer_inorganics"
                                        label={t(
                                            "icvs.humanToxicityCancerInorganics",
                                            "Human Toxicity Cancer Inorganics",
                                        )}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="human_toxicity_cancer_metals"
                                        label={t("icvs.humanToxicityCancerMetals", "Human Toxicity Cancer Metals")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        {/* Eutrophication */}
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "16px" }}
                            >
                                {t("icvs.eutrophication", "Eutrophication")}
                            </Typography>
                            <Stack spacing={2} direction="row" flexWrap="wrap" useFlexGap>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="eutrophication_fresh_water"
                                        label={t("icvs.eutrophicationFreshWater", "Eutrophication Fresh Water")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="eutrophication_marine"
                                        label={t("icvs.eutrophicationMarine", "Eutrophication Marine")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="eutrophication_terrestrial"
                                        label={t("icvs.eutrophicationTerrestrial", "Eutrophication Terrestrial")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="acidification"
                                        label={t("icvs.acidification", "Acidification")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        {/* Ecotoxicity */}
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "16px" }}
                            >
                                {t("icvs.ecotoxicity", "Ecotoxicity")}
                            </Typography>
                            <Stack spacing={2} direction="row" flexWrap="wrap" useFlexGap>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="ecotoxicity_freshwater"
                                        label={t("icvs.ecotoxicityFreshwater", "Ecotoxicity Freshwater")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="ecotoxicity_freshwater_organics"
                                        label={t(
                                            "icvs.ecotoxicityFreshwaterOrganics",
                                            "Ecotoxicity Freshwater Organics",
                                        )}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="ecotoxicity_freshwater_inorganics"
                                        label={t(
                                            "icvs.ecotoxicityFreshwaterInorganics",
                                            "Ecotoxicity Freshwater Inorganics",
                                        )}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="ecotoxicity_freshwater_metals"
                                        label={t("icvs.ecotoxicityFreshwaterMetals", "Ecotoxicity Freshwater Metals")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        {/* Resource Use */}
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "16px" }}
                            >
                                {t("icvs.resourceUse", "Resource Use")}
                            </Typography>
                            <Stack spacing={2} direction="row" flexWrap="wrap" useFlexGap>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="land_use"
                                        label={t("icvs.landUse", "Land Use")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="water_use"
                                        label={t("icvs.waterUse", "Water Use")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="resource_use_fossils"
                                        label={t("icvs.resourceUseFossils", "Resource Use Fossils")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <FloatField
                                        name="resource_use_minerals_metals"
                                        label={t("icvs.resourceUseMineralsMetals", "Resource Use Minerals Metals")}
                                        control={control}
                                        errors={errors}
                                        t={t}
                                    />
                                </Box>
                            </Stack>
                        </Box>
                    </Stack>
                </form>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    padding: "0px 24px 0px 32px",
                    height: "72px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E7E6E4",
                }}
            >
                <Button
                    onClick={handleSubmit(onSubmitHandler)}
                    disabled={!isValid || isProcessing}
                    sx={{
                        width: "150px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        backgroundColor: "primary.main",
                        color: "white",
                        opacity: isValid ? 1 : 0.36,
                        "&:hover": {
                            backgroundColor: "primary.dark",
                        },
                        "&:disabled": {
                            backgroundColor: "primary.main",
                            color: "white",
                        },
                    }}
                >
                    {isProcessing ?
                        <CircularProgress size={20} color="inherit" />
                    : isEditing ?
                        t("common.update", "Update")
                    :   t("common.create", "Create")}
                </Button>
            </Box>
        </Dialog>
    );
};
